// CfoCashAddItem — "I know about this, the model doesn't."
//
// A loan draw, a Q3 tax payment, an equipment purchase, a customer who
// promised Friday: none of it is in QuickBooks yet and none of it is in the
// model. This is the only way to get it into the plan.
//
// Direction is an EXPLICIT choice, never inferred from the category. The
// server maps (category, direction) onto a canonical event_type — see
// `cash_expectation_event_type` — because both the sign and the treasurer row
// are recovered downstream by substring tests over that string, and the old
// `other_cash_event` option matched none of the outflow keywords, so a
// user-entered outflow was booked as an inflow.

import { useState } from 'react'
import type { CashTableRowKey } from './cfoCashTableModel'

export type CfoCashAddItemValue = {
  treasurerRowKey: CashTableRowKey
  direction: 'in' | 'out'
  description: string
  counterparty: string
  expectedDate: string
  amount: number
}

export type CfoCashAddItemProps = {
  weekOptions: { weekStart: string; label: string }[]
  defaultRowKey?: CashTableRowKey
  defaultDate?: string
  busy?: boolean
  error?: string
  onSubmit: (value: CfoCashAddItemValue) => Promise<void> | void
  onCancel: () => void
}

const OUTFLOW_CATEGORIES: { key: CashTableRowKey; label: string }[] = [
  { key: 'payroll', label: 'Payroll' },
  { key: 'rent', label: 'Rent' },
  { key: 'insurance', label: 'Insurance' },
  { key: 'vendors_ap', label: 'Vendor / bill' },
  { key: 'debt', label: 'Debt payment' },
  { key: 'taxes', label: 'Taxes' },
  { key: 'other', label: 'Something else' },
]

const INFLOW_CATEGORIES: { key: CashTableRowKey; label: string }[] = [
  { key: 'collections', label: 'Customer payment' },
  { key: 'other_inflows', label: 'Something else' },
]

export function CfoCashAddItem({
  weekOptions,
  defaultRowKey,
  defaultDate,
  busy = false,
  error,
  onSubmit,
  onCancel,
}: CfoCashAddItemProps) {
  const inferredDirection: 'in' | 'out' =
    defaultRowKey === 'collections' || defaultRowKey === 'other_inflows' ? 'in' : 'out'
  const [direction, setDirection] = useState<'in' | 'out'>(inferredDirection)
  const [rowKey, setRowKey] = useState<CashTableRowKey>(
    defaultRowKey && defaultRowKey !== 'beginning' && defaultRowKey !== 'net' && defaultRowKey !== 'ending'
      ? defaultRowKey
      : inferredDirection === 'in'
        ? 'collections'
        : 'other',
  )
  const [description, setDescription] = useState('')
  const [counterparty, setCounterparty] = useState('')
  const [expectedDate, setExpectedDate] = useState(
    defaultDate || weekOptions[0]?.weekStart || '',
  )
  const [amount, setAmount] = useState('')
  const [localError, setLocalError] = useState('')

  const categories = direction === 'in' ? INFLOW_CATEGORIES : OUTFLOW_CATEGORIES

  const changeDirection = (next: 'in' | 'out') => {
    setDirection(next)
    // Keep the category valid for the new direction rather than silently
    // submitting a Collections row as an outflow.
    const allowed = next === 'in' ? INFLOW_CATEGORIES : OUTFLOW_CATEGORIES
    if (!allowed.some((entry) => entry.key === rowKey)) {
      setRowKey(next === 'in' ? 'collections' : 'other')
    }
  }

  const submit = async () => {
    const parsed = Number(String(amount).replace(/[$,\s]/g, ''))
    if (!Number.isFinite(parsed) || parsed === 0) {
      setLocalError('Enter an amount.')
      return
    }
    if (!expectedDate) {
      setLocalError('Pick a date.')
      return
    }
    if (!description.trim()) {
      setLocalError('Say what this is.')
      return
    }
    setLocalError('')
    await onSubmit({
      treasurerRowKey: rowKey,
      direction,
      description: description.trim(),
      counterparty: counterparty.trim(),
      expectedDate,
      amount: Math.abs(parsed),
    })
  }

  const field: React.CSSProperties = { display: 'grid', gap: 4, fontSize: 12.5 }

  return (
    <section
      className="cfo-ar-card"
      data-testid="cfo-cash-add-item"
      aria-label="Add a cash item"
      style={{ marginTop: 12 }}
    >
      <div className="cfo-ar-section-bar">
        <div className="cfo-ar-section-bar-title">Add something you know about</div>
        <div className="cfo-ar-section-bar-end">
          <button type="button" className="cfo-shell-chip" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>

      {error || localError ? (
        <div className="cfo-shell-notice error" role="alert">
          {error || localError}
        </div>
      ) : null}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 10,
          padding: '8px 8px 12px',
        }}
      >
        <label style={field}>
          <span style={{ color: 'var(--muted)' }}>Money</span>
          <div role="radiogroup" aria-label="Direction" style={{ display: 'flex', gap: 6 }}>
            <button
              type="button"
              role="radio"
              aria-checked={direction === 'out'}
              className="cfo-shell-chip"
              style={direction === 'out' ? { fontWeight: 700 } : undefined}
              onClick={() => changeDirection('out')}
            >
              Going out
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={direction === 'in'}
              className="cfo-shell-chip"
              style={direction === 'in' ? { fontWeight: 700 } : undefined}
              onClick={() => changeDirection('in')}
            >
              Coming in
            </button>
          </div>
        </label>

        <label style={field}>
          <span style={{ color: 'var(--muted)' }}>Category</span>
          <select
            aria-label="Category"
            className="cfo-shell-chip"
            value={rowKey}
            onChange={(event) => setRowKey(event.target.value as CashTableRowKey)}
          >
            {categories.map((entry) => (
              <option key={entry.key} value={entry.key}>
                {entry.label}
              </option>
            ))}
          </select>
        </label>

        <label style={field}>
          <span style={{ color: 'var(--muted)' }}>What is it?</span>
          <input
            aria-label="Description"
            className="cfo-shell-chip"
            value={description}
            placeholder="Equipment purchase"
            onChange={(event) => setDescription(event.target.value)}
          />
        </label>

        <label style={field}>
          <span style={{ color: 'var(--muted)' }}>Who (optional)</span>
          <input
            aria-label="Counterparty"
            className="cfo-shell-chip"
            value={counterparty}
            placeholder="Vendor or customer"
            onChange={(event) => setCounterparty(event.target.value)}
          />
        </label>

        <label style={field}>
          <span style={{ color: 'var(--muted)' }}>Amount</span>
          <input
            aria-label="Amount"
            className="cfo-shell-chip"
            inputMode="decimal"
            value={amount}
            placeholder="40,000"
            onChange={(event) => setAmount(event.target.value)}
          />
        </label>

        <label style={field}>
          <span style={{ color: 'var(--muted)' }}>When</span>
          <input
            aria-label="Expected date"
            className="cfo-shell-chip"
            type="date"
            value={expectedDate}
            onChange={(event) => setExpectedDate(event.target.value)}
          />
        </label>
      </div>

      <div style={{ padding: '0 8px 10px', display: 'flex', gap: 8, alignItems: 'center' }}>
        <button type="button" className="cfo-shell-chip" disabled={busy} onClick={submit}>
          {busy ? 'Adding…' : 'Add to the plan'}
        </button>
        <span style={{ color: 'var(--muted)', fontSize: 12 }}>
          This becomes your number — the model won’t overwrite it.
        </span>
      </div>
    </section>
  )
}

export default CfoCashAddItem
